import React from 'react';
import { useDispatch } from 'react-redux';
import { useDiagramContext } from '../Context/DiagramContext';
import DiagramModal from '../common/DiagramModel';
import CustomModal from '../../../common/components/Modal/CustomModal';
import Modal from '../../../common/components/Modal/Modal';
import ConfirmationModal from '../../../common/components/ConfirmationModal';
import ViewModal from '../../SimulationXPLR/ViewModal';
import TechnologyDataLandscape from '../../EnterpriseContext';
import { Box, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { updateDiagramData, refreshTaxonomy, closeDiagram } from '../store/diagramViewerSlice';
import { updateIdeationFieldState } from '../../NewIdeationXPLR/state';
import ShowToast, { ToastTypeEnum } from '../../../common/components/ShowToast';
import {
  updateProcessName,
  updateVaultStatus,
  deleteCustomProcess,
  deleteIdeation,
} from '../../../services/diagramApi';
import { createIdeationVersion } from '../../NewIdeationXPLR/modules/NewIdeation/Versioning/VersioningManager';

type DiagramStepData = Record<string, { name: string; description: string }>;

const DiagramModals: React.FC = () => {
  const dispatch = useDispatch();
  const {
    activeModal,
    setActiveModal,
    modal,
    isEnterpriseModalOpen,
    setIsEnterpriseModalOpen,
    showUnsavedChangesModal,
    setShowUnsavedChangesModal,
    showDeleteModal,
    setShowDeleteModal,
    showDeleteIdeationModal,
    setShowDeleteIdeationModal,
    showRenameModal,
    setShowRenameModal,
    showVaultModal,
    setShowVaultModal,
    selectedSolutionFromLibrary,
    setSelectedSolutionFromLibrary,
    aiSolutionData,
    setAiSolutionData,
    enterpriseContextApplied,
    setEnterpriseContextApplied,
    renameProcessName,
    setRenameProcessName,
    diagramData,
    handleSaveDiagram,
    handleClose,
    isProcessDiagram,
    bpmnModelerRef,
    containerRef,
    selectedCard,
    handleStepDescriptionUpdate,
    isNavigationGuardModalOpen,
    confirmNavigation,
    cancelNavigation,
    lastSavedXmlRef,
    prevDiagramData,
    onClose,
    setIsDiagramChanged,
    onDiagramChange,
  } = useDiagramContext();

  const isMobile = window.innerWidth <= 600;
  const currentOrg = localStorage.getItem('current_organization') || 'Empty';

  const handleUpdateRenameProcess = async () => {
    try {
      if (!selectedCard?.id) {
        ShowToast({ message: 'Process ID not found', type: ToastTypeEnum.Error });
        return false;
      }
      const success = await updateProcessName(selectedCard?.id, renameProcessName);

      if (success) {
        setRenameProcessName(renameProcessName);
        dispatch(updateIdeationFieldState({ field: 'selectedProcess', value: renameProcessName }));
        
        try {
          dispatch(
            updateDiagramData({
              selectedCard: {
                ...(selectedCard || {}),
                name: renameProcessName,
                taxonomy: {
                  ...((selectedCard && selectedCard.taxonomy) || {}),
                  process: renameProcessName,
                },
              },
              taxonomySubprocessName: renameProcessName,
            })
          );
        } catch (err) {
          console.warn('Failed to update diagram viewer selectedCard name in redux', err);
        }
        
        setShowRenameModal(false);
        dispatch(refreshTaxonomy(true));

        // Trigger Create Version API
        const ideationId = (selectedCard as any)?.useCaseId || (selectedCard as any)?.use_case_id;
        if (ideationId) {
          await createIdeationVersion(ideationId, 'manual', {
            step_name: 'process_diagram',
            opportunity_id: '',
          });
        }

        ShowToast({ message: 'Process name updated successfully', type: ToastTypeEnum.Success });
      } else {
        ShowToast({ message: 'Failed to update process name', type: ToastTypeEnum.Error });
      }
    } catch (error) {
      console.error('Error updating process name:', error);
      ShowToast({ message: 'Failed to update process name', type: ToastTypeEnum.Error });
    }
  };

  const handleToggleVault = async () => {
    if (!selectedSolutionFromLibrary || !selectedSolutionFromLibrary.id) return;

    const newState = !selectedSolutionFromLibrary.vault;
    const success = await updateVaultStatus(currentOrg, selectedSolutionFromLibrary.id, newState);

    if (success) {
      setSelectedSolutionFromLibrary({ ...selectedSolutionFromLibrary, vault: newState });
      setAiSolutionData((prev: any) => (prev ? { ...prev, vault: newState } : null));
      if (newState) {
        ShowToast({ message: 'Solution added to list', type: ToastTypeEnum.Success });
      } else {
        ShowToast({ message: 'Solution removed from list', type: ToastTypeEnum.Success });
      }
    } else {
      ShowToast({ message: 'Unable to process request', type: ToastTypeEnum.Error });
    }
    setShowVaultModal(false);
  };

  const handleDeleteProcess = async () => {
    try {
      if (!selectedCard?.id) {
        ShowToast({ message: 'Process ID not found', type: ToastTypeEnum.Error });
        return false;
      }
      const success = await deleteCustomProcess(currentOrg, selectedCard?.id);

      if (success) {
        dispatch(closeDiagram());
        dispatch(refreshTaxonomy(true));
        setShowDeleteModal(false);
        window.location.reload();
        return true;
      } else {
        ShowToast({ message: 'Failed to delete process', type: ToastTypeEnum.Error });
        return false;
      }
    } catch (error) {
      console.error('Failed to delete process:', error);
      ShowToast({ message: 'Failed to delete process', type: ToastTypeEnum.Error });
      return false;
    }
  };

  const handleConfirmDeleteIdeation = async () => {
    try {
      const useCaseId = (selectedCard as any)?.useCaseId || (selectedCard as any)?.use_case_id;

      if (!useCaseId) {
        ShowToast({ message: 'Use case ID not found', type: ToastTypeEnum.Error });
        return;
      }

      const success = await deleteIdeation(useCaseId);

      if (success) {
        ShowToast({ message: 'Ideation deleted successfully', type: ToastTypeEnum.Success });
        setShowDeleteIdeationModal(false);
        handleClose();
      } else {
        ShowToast({ message: 'Failed to delete ideation', type: ToastTypeEnum.Error });
      }
    } catch (error) {
      console.error('Error deleting ideation:', error);
      ShowToast({ message: 'Error deleting ideation', type: ToastTypeEnum.Error });
      setShowDeleteIdeationModal(false);
    }
  };

  const handleModalClose = () => {
    setActiveModal(null);
  };

  return (
    <>
      {/* Diagram Element Modal */}
      <DiagramModal
        isOpen={activeModal === 'diagram'}
        elementData={modal?.elementData || null}
        activeTab={modal?.activeTab || 'aiHubble'}
        opportunities={modal?.opportunities || []}
        onClose={handleModalClose}
        onTabChange={() => {}}
        onStepDescriptionUpdate={handleStepDescriptionUpdate}
        diagramId={diagramData?.diagram?.id}
        diagramXml={diagramData?.diagram?.xml}
        bpmnModelerRef={bpmnModelerRef}
      />

      {/* Enterprise Context Modal */}
      {isMobile ? (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100vh',
            bgcolor: '#fff',
            overflow: 'auto',
            zIndex: isEnterpriseModalOpen ? 9999 : -1,
            opacity: isEnterpriseModalOpen ? 1 : 0,
            pointerEvents: isEnterpriseModalOpen ? 'auto' : 'none',
            transition: 'opacity 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: '1px solid #e0e0e0',
              flexShrink: 0,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Hackett AI Hubble
            </Typography>
            <IconButton onClick={() => setIsEnterpriseModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <TechnologyDataLandscape minContentHeight="100%" maxContentHeight="100%" />
          </Box>
        </Box>
      ) : (
        <Modal
          isOpen={isEnterpriseModalOpen}
          onClose={() => setIsEnterpriseModalOpen(false)}
          title="Hackett AI Hubble"
          maxWidth="xl"
          width="1600px"
        >
          <TechnologyDataLandscape minContentHeight="65vh" maxContentHeight="65vh" />
        </Modal>
      )}

      {/* Solution Library Modal */}
      <CustomModal
        isOpen={!!selectedSolutionFromLibrary.id}
        onClose={() => setSelectedSolutionFromLibrary({ id: '', name: '', vault: false })}
        title={selectedSolutionFromLibrary.name || 'AI Solution'}
        maxWidth="xl"
        titleActions={
          isMobile ? (
            <IconButton onClick={handleToggleVault}>
              <img src="/images/Vault.png" alt="vault" style={{ width: 24, height: 24 }} />
            </IconButton>
          ) : (
            <Button
              variant="outlined"
              startIcon={<img src="/images/Vault.png" alt="vault" />}
              onClick={handleToggleVault}
            >
              {`${selectedSolutionFromLibrary?.vault ? 'Remove from' : 'Add to'} My List`}
            </Button>
          )
        }
        agentResponse={aiSolutionData}
      >
        <ViewModal
          selectedSolutionId={selectedSolutionFromLibrary.id}
          enterpriseContextApplied={enterpriseContextApplied}
          setEnterpriseContextApplied={setEnterpriseContextApplied}
          aiSolutionData={aiSolutionData}
          setAiSolutionData={setAiSolutionData}
          diagramId={diagramData?.diagram?.id}
        />
      </CustomModal>

      {/* Unsaved Changes Modal */}
      <ConfirmationModal
        isOpen={isNavigationGuardModalOpen || showUnsavedChangesModal}
        title="Save changes"
        description={
          <Box display="flex" alignItems="center" gap={1}>
            <Typography sx={{ color: '#4f4f4f' }}>
              You have unsaved changes. Would you like to save them before closing?
            </Typography>
          </Box>
        }
        primaryButtonText="Save Changes"
        secondaryButtonText="Discard Changes"
        onPrimaryAction={async () => {
          await handleSaveDiagram();
          setShowUnsavedChangesModal(false);

          if (isNavigationGuardModalOpen) {
            confirmNavigation();
          } else {
            if (onClose) {
              onClose();
            } else {
              dispatch(closeDiagram());
            }
          }
        }}
        onSecondaryAction={async () => {
          setShowUnsavedChangesModal(false);

          if (isNavigationGuardModalOpen) {
            confirmNavigation();
          } else {
            if (onClose) {
              const lastSavedXmlCandidate =
                lastSavedXmlRef && 'current' in lastSavedXmlRef
                  ? (lastSavedXmlRef as React.MutableRefObject<string>).current
                  : undefined;
              const previousXml =
                (typeof lastSavedXmlCandidate === 'string' ? lastSavedXmlCandidate : undefined) ||
                diagramData?.diagram?.xml ||
                '';
              const previousData =
                (prevDiagramData as DiagramStepData | undefined) ||
                (diagramData?.diagram?.data as DiagramStepData | undefined) ||
                ({} as DiagramStepData);
              const previousId = diagramData?.diagram?.id;

              if (!previousXml || !previousId || !containerRef.current) {
                return;
              }

              dispatch(
                updateDiagramData({
                  DiagramData: {
                    diagram: {
                      id: previousId,
                      xml: previousXml,
                      data: previousData,
                    },
                  },
                })
              );

              if (location.pathname?.includes('ideation-xplr')) {
                if (bpmnModelerRef.current && previousXml) {
                  try {
                    await bpmnModelerRef.current.importXML(previousXml);
                  } catch (err) {
                    console.error('Failed to revert diagram XML:', err);
                  }
                }
                // Reset both change flags so Save changes 1 no longer triggers
                setIsDiagramChanged(false);
                if (onDiagramChange) onDiagramChange(false);
                return;
              }
            }

            if (onClose) {
              onClose();
            } else {
              dispatch(closeDiagram());
            }
          }
        }}
        onClose={() => {
          if (isNavigationGuardModalOpen) {
            cancelNavigation();
          } else {
            setShowUnsavedChangesModal(false);
          }
          // Do not discard changes on close
        }}
        width="626px"
      />

      {/* Rename Process Modal */}
      <Modal
        isOpen={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        title="Rename Process"
        maxWidth="sm"
        width="100%"
      >
        <Box
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '14px', color: '#2c2c2c', mb: 1.5, fontWeight: 500 }}>
              Process Name
            </Typography>
            <input
              type="text"
              value={renameProcessName}
              onChange={(e) => setRenameProcessName(e.target.value)}
              placeholder="Enter process name"
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '16px',
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Poppins, sans-serif',
                boxSizing: 'border-box',
                outline: 'none',
                transition: 'border-color 0.3s',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1A55F9';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#E0E0E0';
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={() => {
                console.log('Process renamed to:', renameProcessName);
                handleUpdateRenameProcess();
              }}
              sx={{
                textTransform: 'none',
                bgcolor: '#2054F4',
                borderRadius: '6px',
                px: 4,
                py: 1.5,
                fontWeight: 600,
                fontSize: '14px',
                '&:hover': { bgcolor: '#1A45C6' },
              }}
            >
              Update
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Delete Confirmation Modals */}
      <ConfirmationModal
        isOpen={isProcessDiagram ? showDeleteIdeationModal : showDeleteModal}
        title="Delete Process"
        description={
          <Typography sx={{ color: '#4f4f4f' }}>
            {isProcessDiagram
              ? 'Are you sure you want to delete this entire ideation? This action cannot be undone.'
              : 'Do you wish to delete this process? This action is permanent.'}
          </Typography>
        }
        primaryButtonText="Delete"
        onPrimaryAction={() => {
          if (isProcessDiagram) {
            handleConfirmDeleteIdeation();
          } else {
            handleDeleteProcess();
          }
        }}
        onClose={() => {
          isProcessDiagram ? setShowDeleteIdeationModal(false) : setShowDeleteModal(false);
        }}
        width="781px"
      />

      {/* Vault Modal */}
      <Modal
        isOpen={showVaultModal}
        onClose={() => setShowVaultModal(false)}
        title={selectedSolutionFromLibrary?.vault ? 'Remove from My List' : 'Add to My List'}
        maxWidth="sm"
      >
        <Box sx={{ bgcolor: '#F9FBFF', borderRadius: '12px', p: 3 }}>
          <Typography sx={{ fontSize: '16px', color: '#2c2c2c', mb: 2 }}>
            Are you sure you want to {selectedSolutionFromLibrary?.vault ? 'remove' : 'add'} this AI
            Solution?
          </Typography>
          <Button
            variant="contained"
            onClick={handleToggleVault}
            sx={{ textTransform: 'none', bgcolor: '#2054F4', px: 3, py: 1 }}
          >
            {selectedSolutionFromLibrary?.vault ? 'Remove' : 'Add'}
          </Button>
        </Box>
      </Modal>
    </>
  );
};

export default DiagramModals;
